import { useState, useEffect } from "react";
import {
  getUISettings,
  updateUISettings,
} from "../../services/portfolioService";

interface UISettingsPanelProps {
  className?: string;
}

export function UISettingsPanel({ className = "" }: UISettingsPanelProps) {
  const [refreshInterval, setRefreshInterval] = useState<number>(300);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Load current settings
  useEffect(() => {
    async function load() {
      try {
        setIsLoading(true);
        const settings = await getUISettings();
        setRefreshInterval(settings.refresh_interval_seconds);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  // Save settings
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);
      await updateUISettings(refreshInterval);
      setSuccess("Settings saved successfully");
      setIsDirty(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Predefined interval options
  const intervalOptions = [
    { value: 30, label: "30 seconds" },
    { value: 60, label: "1 minute" },
    { value: 120, label: "2 minutes" },
    { value: 300, label: "5 minutes" },
    { value: 600, label: "10 minutes" },
    { value: 900, label: "15 minutes" },
    { value: 1800, label: "30 minutes" },
  ];

  if (isLoading) {
    return (
      <div className={`p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <div className="animate-spin w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full" />
          </div>
          <span className="text-surface-400">Loading UI settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-cyan-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <div className="text-sm font-medium text-surface-100">
              UI Settings
            </div>
            <div className="text-xs text-surface-400">
              Configure display preferences
            </div>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
          {success}
        </div>
      )}

      {/* Refresh Interval Setting */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Performance Chart Refresh Interval
          </label>
          <p className="text-xs text-surface-500 mb-3">
            How often the performance chart automatically refreshes data from the server.
          </p>
          <div className="flex flex-wrap gap-2">
            {intervalOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setRefreshInterval(option.value);
                  setIsDirty(true);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  refreshInterval === option.value
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                    : "bg-surface-700/50 text-surface-400 border border-surface-600 hover:bg-surface-600/50 hover:text-surface-300"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom value input */}
        <div>
          <label className="block text-xs text-surface-500 mb-1">
            Or enter custom value (30-1800 seconds):
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min={30}
              max={1800}
              value={refreshInterval}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 30;
                setRefreshInterval(Math.max(30, Math.min(1800, val)));
                setIsDirty(true);
              }}
              className="w-24 px-3 py-1.5 rounded-lg bg-surface-800 border border-surface-600 text-surface-200 text-sm focus:border-cyan-500 focus:outline-none"
            />
            <span className="text-sm text-surface-500">seconds</span>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-2">
          <button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              isDirty && !isSaving
                ? "bg-cyan-500 text-white hover:bg-cyan-600"
                : "bg-surface-700 text-surface-500 cursor-not-allowed"
            }`}
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                Saving...
              </span>
            ) : (
              "Save Settings"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
